import { createContext, PropsWithChildren, useMemo, useRef } from 'react';

import { useResource, useRouter } from 'react-resource-router';

import { accountNavigationResource } from 'src/sections/account/account-navigation-resource';
import { projectNavigationResource } from 'src/sections/project/project-navigation-resource';
import { repositoryNavigationResource } from 'src/sections/repository/repository-navigation-resource';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';

export type CurrentData = {
  workspace?: BB.Workspace;
  project?: BB.Project;
  repository?: BB.Repository;
};

export const CurrentDataContext = createContext<CurrentData>({
  workspace: undefined,
  project: undefined,
  repository: undefined,
});

type Props = PropsWithChildren<{
  initialContext: CurrentData;
}>;

export const CurrentDataProvider = (props: Props): JSX.Element => {
  // We get the initial context from the server via app data. As soon as we
  // get any content from a resource we should ignore the initial values so
  // we don't pass stale data.
  const hasUpdatedRef = useRef(false);
  const [state] = useRouter();
  const cachedValue = useRef<CurrentData>({
    workspace: undefined,
    project: undefined,
    repository: undefined,
  });
  const initialValue = props.initialContext;

  const { data: workspaceData, loading: isWorkspaceLoading } = useResource(
    workspaceNavigationResource
  );
  const { data: projectData, loading: isProjectLoading } = useResource(
    projectNavigationResource
  );
  const { data: repoData, loading: isRepoLoading } = useResource(
    repositoryNavigationResource
  );
  const { data: accountData, loading: isAccountLoading } = useResource(
    accountNavigationResource
  );

  const workspace =
    workspaceData?.workspace ||
    projectData?.project.workspace ||
    repoData?.repository.workspace ||
    accountData?.workspace;

  const project = projectData?.project || repoData?.repository.project;
  const repository = repoData?.repository;

  const resourceValue = useMemo(() => {
    return {
      workspace,
      project,
      repository,
    };
  }, [workspace, project, repository]);

  if (workspaceData || projectData || repoData || accountData) {
    hasUpdatedRef.current = true;
  }
  let value: CurrentData;
  if (!hasUpdatedRef.current) {
    // if we haven't loaded any data yet, use the initial context from the server
    value = initialValue;
  } else if (
    isWorkspaceLoading ||
    isProjectLoading ||
    isRepoLoading ||
    isAccountLoading
  ) {
    // if data is loading, use the cached value; we load features based on
    // this current data, so we should try to avoid making changes until we
    // know the data has changed
    value = cachedValue.current;
  } else if (state.route.name === 'ROOT') {
    // The ROOT route is a basic redirect but does not have any resources
    // configured. We can use the cached value and then depend on the
    // resources from the redirected route.
    value = cachedValue.current;
  } else {
    // use values from the resources
    value = resourceValue;
  }
  cachedValue.current = value;

  return (
    <CurrentDataContext.Provider value={value}>
      {props.children}
    </CurrentDataContext.Provider>
  );
};
